import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Loader2, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  performStartProjectChannelCall,
  type ProjectCallCandidate,
  type StartProjectChannelCallResult,
} from './project-call-actions';
import {
  resolveProjectChatVideoRecipients,
  type ProjectCallMember,
  type ProjectCallRosterProject,
} from './project-chat-recipients';

export interface ProjectHeaderVideoCallChannel {
  id: string;
  type: string;
  projectId: string | null;
}

export interface ProjectHeaderVideoCallProps {
  channel: ProjectHeaderVideoCallChannel;
  project: ProjectCallRosterProject | null | undefined;
  teamMembers: readonly ProjectCallMember[];
  currentUserId: number;
  rosterPending: boolean;
  rosterError: boolean;
  callBusy?: boolean;
  startCall: ProjectHeaderStartCall;
}

export type ProjectHeaderStartCall = (args: {
  channelId: string;
  type: 'audio' | 'video';
  inviteeUserIds: number[];
  projectId?: string | null;
}) => Promise<void> | void;

export interface ProjectHeaderCallStartDeps {
  startCall: ProjectHeaderStartCall;
  toast: (message: {
    title: string;
    description: string;
    variant?: 'default' | 'destructive';
  }) => void;
}

/**
 * The imperative part of the header action is kept separate from the button
 * so its channel/project/id wiring can be checked without mounting ChatDock.
 */
export async function performProjectHeaderVideoCall(
  channel: ProjectHeaderVideoCallChannel,
  inviteeUserIds: number[],
  currentUserId: number,
  deps: ProjectHeaderCallStartDeps,
): Promise<StartProjectChannelCallResult> {
  const safeInviteeUserIds = Array.from(
    new Set(inviteeUserIds.filter((id) => id !== currentUserId)),
  );
  const candidate: ProjectCallCandidate = {
    projectId: channel.projectId ?? '',
    existingChannel: { id: channel.id },
    assignedArtists: safeInviteeUserIds.map((id) => ({ id })),
    admins: [],
  };

  return performStartProjectChannelCall(candidate, 'video', safeInviteeUserIds, {
    ensureChannelId: async () => channel.id,
    startCall: deps.startCall,
    toast: deps.toast,
  });
}

function unavailableReason(
  kind: 'loading' | 'error' | 'missing-project' | 'no-recipient',
  noProjectManager: boolean,
): string {
  if (kind === 'loading') return 'Video call recipients are still loading';
  if (kind === 'error') return 'Video call unavailable: the project or team roster failed to load';
  if (kind === 'missing-project') return 'Video call unavailable: the project assignment roster is unavailable';
  if (noProjectManager) {
    return 'Video call unavailable: no other active assignee and the BEN fallback is unavailable';
  }
  return 'Video call unavailable: no other active assignee or active admin BEN is available';
}

/**
 * Compact video action used only in the expanded left project/team header.
 * The parent deliberately decides where it is rendered; this component does
 * not expose a recipient picker and never broadens a project call to admins.
 */
export function ProjectHeaderVideoCall({
  channel,
  project,
  teamMembers,
  currentUserId,
  rosterPending,
  rosterError,
  callBusy = false,
  startCall,
}: ProjectHeaderVideoCallProps) {
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);
  const pendingRef = useRef(false);

  const resolution = useMemo(() => {
    if (rosterPending || rosterError || !project) return null;
    return resolveProjectChatVideoRecipients(project, teamMembers, currentUserId);
  }, [project, rosterError, rosterPending, teamMembers, currentUserId]);

  const recipientNames = resolution?.kind === 'ready' ? resolution.recipientNames : [];
  const noProjectManager = resolution?.kind === 'unavailable' && resolution.reason === 'no-project-manager';
  const readyLabel = `Start video call with ${recipientNames.join(', ')}`;
  const disabledReason = rosterPending
    ? unavailableReason('loading', false)
    : rosterError
      ? unavailableReason('error', false)
      : !project
        ? unavailableReason('missing-project', false)
        : resolution?.kind === 'unavailable'
          ? unavailableReason('no-recipient', noProjectManager)
          : null;
  const label = disabledReason
    ?? (callBusy
      ? `${readyLabel}; another call is already starting or active`
      : readyLabel);
  const isDisabled = isPending || callBusy || !!disabledReason;

  const handleClick = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isDisabled || pendingRef.current || resolution?.kind !== 'ready') return;

    pendingRef.current = true;
    setIsPending(true);
    try {
      await performProjectHeaderVideoCall(
        channel,
        resolution.recipients.map((member) => member.id),
        currentUserId,
        { startCall, toast },
      );
    } catch (error) {
      toast({
        title: 'Could not start video call',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, [channel, isDisabled, resolution, startCall, toast]);

  return (
    <span
      className="shrink-0"
      title={label}
      onClick={(event) => event.stopPropagation()}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-6 w-6 shrink-0',
          isDisabled ? 'text-muted-foreground/50' : 'text-muted-foreground hover:text-sky-400',
        )}
        onClick={handleClick}
        disabled={isDisabled}
        title={label}
        aria-label={label}
        aria-busy={isPending}
        data-testid="project-header-video-call"
      >
        {isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Video className="h-3.5 w-3.5" />
        )}
      </Button>
    </span>
  );
}
